import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "../ManualDeliveries.css";

const GAP = 4;

// The "⋯" row menu. Fixed to the viewport from the trigger's rect so the table never clips it, flipped above the
// trigger when there is no room below, and scrolling inside itself when it fits on neither side. It closes on any
// scroll, resize, outside press, Tab or Escape. Escape and Tab ask for focus back on the trigger (onClose(true)),
// so Tab carries on from there. A press on triggerRef (optional) is not an outside press: the trigger toggles.
// Disabled items stay focusable (aria-disabled) so their reason is read out.
export default function RowMenu({ anchorRect, items, label, onSelect, onClose, triggerRef = null }) {
  const ref = useRef(null);
  const [place, setPlace] = useState({ top: anchorRect.bottom + GAP, maxHeight: null });

  useLayoutEffect(() => {
    const height = ref.current ? ref.current.offsetHeight : 0;
    const below = anchorRect.bottom + GAP;
    const above = anchorRect.top - GAP - height;
    const roomBelow = window.innerHeight - below - GAP;
    const roomAbove = anchorRect.top - 2 * GAP;
    if (height <= roomBelow + GAP) setPlace({ top: below, maxHeight: null });
    else if (above >= 0) setPlace({ top: above, maxHeight: null });
    else if (roomBelow >= roomAbove) setPlace({ top: below, maxHeight: roomBelow });
    else setPlace({ top: GAP, maxHeight: roomAbove });
    const first = ref.current && ref.current.querySelector('[role="menuitem"]');
    // preventScroll: a scroll here would close the menu straight away.
    if (first) first.focus({ preventScroll: true });
  }, [anchorRect]);

  useEffect(() => {
    const onScroll = () => onClose(false);
    const onPointer = (event) => {
      const trigger = triggerRef && triggerRef.current;
      if (trigger && trigger.contains(event.target)) return;
      if (ref.current && !ref.current.contains(event.target)) onClose(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    document.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [onClose, triggerRef]);

  const onKeyDown = (event) => {
    const nodes = Array.from(ref.current.querySelectorAll('[role="menuitem"]'));
    const focusAt = (index) => nodes[(index + nodes.length) % nodes.length].focus({ preventScroll: true });
    const current = nodes.indexOf(document.activeElement);
    if (event.key === "Escape") {
      event.preventDefault();
      onClose(true);
    } else if (event.key === "Tab") {
      onClose(true);
    } else if (!nodes.length) {
      return;
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      focusAt(current + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusAt(current - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusAt(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusAt(nodes.length - 1);
    }
  };

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      className="md-menu"
      style={{
        top: place.top,
        right: window.innerWidth - anchorRect.right,
        maxHeight: place.maxHeight === null ? undefined : place.maxHeight,
        overflowY: place.maxHeight === null ? undefined : "auto",
      }}
      onKeyDown={onKeyDown}
    >
      {items.map((item) => (
        <button
          key={item.action}
          type="button"
          role="menuitem"
          tabIndex={-1}
          aria-disabled={item.disabled ? "true" : undefined}
          className={`md-menu__item${item.danger ? " md-menu__item--danger" : ""}`}
          onClick={() => {
            if (!item.disabled) onSelect(item.action);
          }}
        >
          <span>{item.label}</span>
          {item.hint ? <span className="md-menu__hint">{item.hint}</span> : null}
        </button>
      ))}
    </div>
  );
}
